/**
 * useSeasonalThemes — SEASON-1's setting, and the season it shows.
 *
 * The setting lives in localStorage under `orbit.seasonalThemes`, the
 * key iOS keeps in UserDefaults. Unset is ON: a studio sees Halloween in
 * October unless it turns the seasons off (Rennan: on by default).
 *
 * A preview shows a season outside its dates, for review: open any app
 * page with `?season=christmas` (or `halloween`), or pick one in
 * Settings → Appearance in developer mode. It lasts for the tab
 * (sessionStorage) and `?season=off` clears it. Like iOS, a preview
 * only shows while the setting is on.
 *
 * `useSeason()` is for the shell, which decides and publishes the
 * answer (`publishActiveSeason`); everything else reads it with
 * `useActiveSeason()` — Nova in a dialog or the launcher included — and
 * it's null whenever the shell isn't up, so a Nova on the marketing
 * site never dresses up.
 */
import { createContext, createElement, useCallback, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { isSeason, seasonOn, type Season } from '@/lib/seasons';

const STORAGE_KEY = 'orbit.seasonalThemes';
const PREVIEW_KEY = 'orbit.seasonalPreview';

// Private mode can refuse storage; the switch still works for the tab.
let memoryEnabled = true;
let memoryPreview: Season | null = null;

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Another tab flipping the switch.
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function readEnabled(): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? memoryEnabled : raw !== '0';
  } catch {
    return memoryEnabled;
  }
}

function readPreview(): Season | null {
  try {
    const raw = window.sessionStorage.getItem(PREVIEW_KEY);
    // A full storage can refuse the write but still answer the read.
    return isSeason(raw) ? raw : memoryPreview;
  } catch {
    return memoryPreview;
  }
}

/** Show `season` outside its dates in this tab (null to stop). */
export function setSeasonPreview(season: Season | null) {
  memoryPreview = season;
  try {
    if (season) window.sessionStorage.setItem(PREVIEW_KEY, season);
    else window.sessionStorage.removeItem(PREVIEW_KEY);
  } catch {
    /* kept in memory above */
  }
  emit();
}

/** `?season=halloween|christmas` starts a preview; `?season=off` ends it.
 *  Read once, as the app loads, so the first frame is already right. */
function readPreviewParam() {
  if (typeof window === 'undefined') return;
  const value = new URLSearchParams(window.location.search).get('season');
  if (value === null) return;
  if (isSeason(value)) setSeasonPreview(value);
  else if (value === 'off' || value === 'none' || value === '') setSeasonPreview(null);
}
readPreviewParam();

/** `[on, setOn]` — the Seasonal themes switch. */
export function useSeasonalThemes(): [boolean, (next: boolean) => void] {
  const enabled = useSyncExternalStore(subscribe, readEnabled, () => true);
  const set = useCallback((next: boolean) => {
    memoryEnabled = next;
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* kept in memory above */
    }
    emit();
  }, []);
  return [enabled, set];
}

/** The previewed season, if any. */
export function useSeasonPreview(): Season | null {
  return useSyncExternalStore(subscribe, readPreview, () => null);
}

/**
 * The season showing now, or null (the setting is off, or no season
 * today). Re-reads the calendar every five minutes, so an app left open
 * on October 31 is out of Halloween by November.
 */
export function useSeason(): Season | null {
  const [enabled] = useSeasonalThemes();
  const preview = useSeasonPreview();
  const [onCalendar, setOnCalendar] = useState<Season | null>(() => seasonOn());
  useEffect(() => {
    const id = window.setInterval(() => setOnCalendar(seasonOn()), 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!enabled) return null;
  return preview ?? onCalendar;
}

// ── The season the app is showing ──────────────────────────────────
// Set by the shell while it's mounted; null otherwise.
let activeSeason: Season | null = null;
const activeListeners = new Set<() => void>();

export function publishActiveSeason(season: Season | null) {
  if (season === activeSeason) return;
  activeSeason = season;
  activeListeners.forEach((listener) => listener());
}

function subscribeActive(listener: () => void) {
  activeListeners.add(listener);
  return () => {
    activeListeners.delete(listener);
  };
}

/**
 * A subtree that shows a season of its own, whatever the app is doing —
 * the marketing page's rebuilt screens (GoogieStage), where Nova dresses
 * for the season on show. `undefined` means "no override".
 */
const SeasonOverride = createContext<Season | null | undefined>(undefined);

export function SeasonOverrideProvider({ season, children }: { season: Season | null; children: ReactNode }) {
  return createElement(SeasonOverride.Provider, { value: season }, children);
}

/** The season the signed-in app is showing — null anywhere else. */
export function useActiveSeason(): Season | null {
  const override = useContext(SeasonOverride);
  const live = useSyncExternalStore(
    subscribeActive,
    () => activeSeason,
    () => null,
  );
  return override !== undefined ? override : live;
}
